import type { OtpUsageType } from './types';

/**
 * Values the three screens share.
 *
 * Nothing here is a product setting. `AUTH_METHODS`, the code's validity, and
 * the code's length all arrive from `GET /api/v1/authConfiguration` and are
 * never written down in this repo — a member named here would put a catalog
 * decision behind a rebuild. What *is* here is the wire's own shape: the two
 * usage values these screens send, the route table, and the length bounds
 * `backend-auth`'s request models already enforce.
 */

/** The three routes of the auth shell, in the order an operator meets them. */
export const AUTH_ROUTES = {
  login: '/login',
  verify: '/login/verify',
  recover: '/recover',
} as const;

/**
 * The two `OtpUsageTypeEnum` members these screens issue. The third,
 * `CONTACT_NUMBER_CHANGE`, belongs to the subscriber host's settings surface —
 * this console has no account settings at all (PRD-TDD §6.4).
 */
export const OTP_USAGE: Record<'login' | 'recovery', OtpUsageType> = {
  login: 'LOGIN',
  recovery: 'PASSWORD_RECOVERY',
} as const;

/**
 * Length bounds mirrored from `backend-auth`'s `src/api/v1/schemas/common.py` —
 * `PhoneField`, `CodeField`, `PasswordField`. They are wire-contract
 * constraints, not configured behaviour: the server refuses outside them
 * whatever this form does, and repeating them here only moves the refusal
 * earlier so the operator is not made to wait for a round trip to be told.
 */
export const PHONE_MAX_LENGTH = 32;
export const CODE_MAX_LENGTH = 16;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * The theme key, shared with both sibling frontends so one choice survives the
 * hop between hosts. The auth screens carry the toggle because it assumes no
 * session — it is the only control besides the mark that survives the half
 * shell.
 */
export const AUTH_SHELL_MAX_INLINE_SIZE = '26rem';
